import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  alpha,
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Grid,
} from "@mui/material";
import {
  GitHub,
  OpenInNew,
  CheckCircleOutline,
  EventAvailable,
  Person,
  Group,
  Build,
  Update,
  ArrowBack,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import SeoHelmet from "../../../Components/SeoHelmet";
import { liveProjectsData } from "../../../Data/liveProjects";
import { trainProjectsData } from "../../../Data/trainProjects";

export default function ProjectDetail() {
  const theme = useTheme();
  const { code } = useParams();

  const project = code?.startsWith("train")
    ? trainProjectsData?.find((pr) => pr?._id === code)
    : liveProjectsData?.find((pr) => pr?._id === code);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [code]);

  if (!project) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h5" fontWeight={500} color="error">
          Project not found
        </Typography>
      </Box>
    );
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case "frontend":
        return "Frontend Developer";
      case "backend":
        return "Backend Developer";
      case "fullstack":
        return "Fullstack Developer";
      default:
        return role;
    }
  };

  const getContributionLabel = (type) => {
    switch (type) {
      case "full":
        return "Full Development (0 to 100)";
      case "update":
        return "Update & Maintenance";
      default:
        return type;
    }
  };

  const getCollaborationLabel = (type) => {
    switch (type) {
      case "alone":
        return "Solo Project";
      case "withTeam":
        return "Team Collaboration";
      default:
        return type;
    }
  };

  // Mapped Elements
  const featureItems = project?.keyFeatures?.map((feature, i) => (
    <motion.div
      key={i}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
    >
      <Stack direction="row" alignItems="flex-start" gap={2} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0.8,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${alpha(project.color, 0.1)} 0%, transparent 100%)`,
            border: `1px solid ${alpha(project.color, 0.2)}`,
            color: project.color,
          }}
        >
          <CheckCircleOutline sx={{ fontSize: "1.1rem" }} />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.7,
            pt: 0.5,
            fontWeight: 400,
          }}
        >
          {feature}
        </Typography>
      </Stack>
    </motion.div>
  ));

  const toolItems = project?.tools?.map((item, index) => (
    <motion.div
      key={item}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.5 + index * 0.05 }}
      whileHover={{
        scale: 1.05,
        y: -3,
        boxShadow: `0 8px 15px ${alpha(project.color, 0.3)}`,
        transition: { type: "spring", stiffness: 400 },
      }}
      whileTap={{ scale: 0.95 }}
      style={{ borderRadius: "20px" }}
    >
      <Box
        sx={{
          px: 1.8,
          py: 0.6,
          borderRadius: 20,
          fontSize: "0.75rem",
          fontWeight: 500,
          color: "text.primary",
          border: `1px solid ${alpha(project.color, 0.5)}`,
          background: `linear-gradient(145deg, 
            ${alpha(theme.palette.background.paper, 0.8)}, 
            ${alpha(theme.palette.background.default, 0.8)})`,
          backdropFilter: "blur(10px)",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background: `linear-gradient(90deg, transparent, ${alpha(project.color, 0.6)}, transparent)`,
            transition: "left 0.5s",
          },
          "&:hover::before": {
            left: "100%",
          },
        }}
      >
        {item}
      </Box>
    </motion.div>
  ));

  const imageItems = project?.images?.map((img, idx) => (
    <motion.div
      key={idx}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: idx * 0.15 }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "250px", sm: "400px", md: "500px" },
          borderRadius: 4,
          overflow: "hidden",
          mb: 4,
          border: `1px solid ${alpha(project.color, 0.15)}`,
          background: "rgba(255,255,255,0.02)",
          "&:hover": {
            borderColor: alpha(project.color, 0.5),
            "& img": { transform: "scale(1.03)" },
            "& .img-overlay": { opacity: 1 },
          },
        }}
      >
        <img
          src={img}
          alt={`${project.title} Preview ${idx + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        />
        <Box
          className="img-overlay"
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 50%)`,
            opacity: 0,
            transition: "opacity 0.4s ease",
            display: "flex",
            alignItems: "flex-end",
            p: 4,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#fff", fontWeight: 500, letterSpacing: 0.5 }}
          >
            {project.title} - View {idx + 1}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  ));

  const infoCards = [
    {
      title: "My Role",
      value: getRoleLabel(project.myRole),
      icon: <Person />,
    },
    {
      title: "Team Structure",
      value:
        project.teamSize === 1
          ? "Solo Developer"
          : `${project.teamSize} Members`,
      subValue: getCollaborationLabel(project.collaborationType),
      icon: project.collaborationType === "alone" ? <Person /> : <Group />,
    },
    {
      title: "Contribution",
      value: getContributionLabel(project.contributionType),
      icon: project.contributionType === "full" ? <Build /> : <Update />,
    },
    {
      title: "Timeline",
      value: project.doneTime,
      icon: <EventAvailable />,
    },
  ];

  const renderInfoCards = infoCards?.map((card, idx) => (
    <motion.div
      key={idx}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 + idx * 0.1 }}
    >
      <Box
        sx={{
          p: 2.5,
          borderRadius: 4,
          background:
            "linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${alpha(project.color, 0.1)}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateX(-4px)",
            borderColor: alpha(project.color, 0.3),
            background: alpha(project.color, 0.03),
            boxShadow: `-8px 8px 15px -10px ${alpha(project.color, 0.15)}`,
            "& .icon-wrapper": {
              background: project.color,
              color: "#fff",
              boxShadow: `0 0 12px ${alpha(project.color, 0.5)}`,
            },
          },
        }}
      >
        <Box
          className="icon-wrapper"
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(project.color, 0.1),
            color: project.color,
            transition: "all 0.4s ease",
            "& svg": { fontSize: "1.4rem" },
          }}
        >
          {card.icon}
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 1,
              fontWeight: 500,
              fontSize: "0.65rem",
            }}
          >
            {card.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              lineHeight: 1.3,
              mt: 0.3,
            }}
          >
            {card.value}
          </Typography>
          {card.subValue && (
            <Typography
              variant="caption"
              sx={{ color: project.color, fontWeight: 400, fontSize: "0.7rem" }}
            >
              {card.subValue}
            </Typography>
          )}
        </Box>
      </Box>
    </motion.div>
  ));

  return (
    <>
      <SeoHelmet
        title={`${project.title} | Pouriya Sabouri`}
        description={`${project.description.substring(0, 150)}...`}
        keywords={`${project.title}, react project, ${project.tools?.join(", ")}`}
        ogTitle={project.title}
        ogDescription={project.description.substring(0, 120)}
        ogImage={project.images?.[0] || "/og-image.jpg"}
        ogType="article"
      />

      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          overflowX: "hidden",
          pb: 10,
        }}
      >
        {/* Dynamic Animated Background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <motion.div
            animate={{ x: [0, 60, -40, 0], y: [0, -50, 40, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: "5%",
              left: "10%",
              width: "45vw",
              height: "45vw",
              background: `radial-gradient(circle, ${alpha(project.color, 0.1)} 0%, transparent 70%)`,
              filter: "blur(60px)",
            }}
          />
          <motion.div
            animate={{ x: [0, -50, 30, 0], y: [0, 60, -30, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              bottom: "10%",
              right: "5%",
              width: "50vw",
              height: "50vw",
              background: `radial-gradient(circle, ${alpha(project.color, 0.08)} 0%, transparent 70%)`,
              filter: "blur(80px)",
            }}
          />
        </Box>

        {/* Floating Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{ position: "fixed", top: "2rem", left: "2rem", zIndex: 100 }}
        >
          <IconButton
            component={Link}
            to="/projects"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "text.primary",
              transition: "all 0.3s ease",
              "&:hover": {
                background: alpha(project.color, 0.15),
                borderColor: project.color,
                color: project.color,
                transform: "translateX(-4px)",
              },
            }}
          >
            <ArrowBack sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </motion.div>

        <Container
          maxWidth="xxl"
          sx={{ pt: { xs: 12, md: 14 }, position: "relative", zIndex: 1 }}
        >
          {/* Hero Section */}
          <Box sx={{ mb: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "3.2rem", lg: "4rem" },
                  lineHeight: 1.1,
                  mb: 2,
                  letterSpacing: "-0.5px",
                  background: `linear-gradient(135deg, #fff 0%, ${project.color} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {project.title}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  href={project.liveUrl}
                  target="_blank"
                  startIcon={<OpenInNew sx={{ fontSize: "1rem" }} />}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    background: project.color,
                    color: "#fff",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    boxShadow: `0 6px 15px -5px ${alpha(project.color, 0.5)}`,
                    "&:hover": {
                      background: alpha(project.color, 0.8),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Live Demo
                </Button>
                {project.githubUrl && (
                  <Button
                    variant="outlined"
                    href={project.githubUrl}
                    target="_blank"
                    startIcon={<GitHub sx={{ fontSize: "1rem" }} />}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      borderColor: alpha(project.color, 0.4),
                      color: "text.primary",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: project.color,
                        background: alpha(project.color, 0.08),
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Source Code
                  </Button>
                )}
              </Stack>
            </motion.div>
          </Box>

          {/* Grid Layout: Content (Left) & Metadata (Right) */}
          <Grid container spacing={6} sx={{ mb: 8 }}>
            {/* Left Column: Description, Features, Stack */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
                >
                  Overview
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.95rem",
                    fontWeight: 400,
                    lineHeight: 1.8,
                    mb: 5,
                  }}
                >
                  {project.longDesc || project.description}
                </Typography>
              </motion.div>

              <Box sx={{ mb: 5 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2.5, color: "text.primary" }}
                >
                  Key Features
                </Typography>
                {featureItems}
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2.5, color: "text.primary" }}
                >
                  Technologies Used
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1.2}>
                  {toolItems}
                </Stack>
              </Box>
            </Grid>

            {/* Right Column: Info Cards */}
            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={2} sx={{ position: "sticky", top: "2rem" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: "text.primary",
                    display: { xs: "none", lg: "block" },
                  }}
                >
                  Project Details
                </Typography>
                {renderInfoCards}
              </Stack>
            </Grid>
          </Grid>

          {/* Image Gallery */}
          <Container>
            {project.images && project.images.length > 0 && (
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 4,
                    textAlign: "center",
                    color: "text.primary",
                  }}
                >
                  Gallery
                </Typography>
                <Box>{imageItems}</Box>
              </Box>
            )}
          </Container>
        </Container>

        {/* Scroll to Top Button */}
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              position: "fixed",
              bottom: "2rem",
              right: "2rem",
              zIndex: 100,
            }}
          >
            <IconButton
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                background: project.color,
                color: "#fff",
                boxShadow: `0 8px 20px -5px ${alpha(project.color, 0.5)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: alpha(project.color, 0.8),
                  transform: "translateY(-4px)",
                },
              }}
            >
              <KeyboardArrowUp sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          </motion.div>
        </AnimatePresence>
      </Box>
    </>
  );
}
